<template>
  <v-dialog
    :value="dialog"
    @input="$store.commit('product/CLOSE_DIALOG', 'details')"
    max-width="750"
  >
    <div
      class="background-color-white position-relative px-7 py-10"
      id="keep-search"
    >
      <CloseDialogButton
        :close="() => $store.commit('product/CLOSE_DIALOG', 'details')"
      />
      <Title
        :category="product.categories[0]"
        :department="product.parent_categories[0]"
        closeDialog="details"
      />
      <SurgeonGeneralsWarning
        v-if="product.tobacco_warning"
        :text="product.tobacco_warning"
      />
      <AlcoholProductsRequireIdWarning v-if="product.is_alcohol" />
      <v-container>
        <v-row align="center">
          <v-col>
            <div class="align-center d-flex image-container justify-center">
              <FavoriteIcon
                class="favorite-button-position"
                :upc="product.upc"
              />
              <img
                v-if="product.image"
                :alt="product.name"
                :src="product.image"
                class="product-image-size-lg"
              />
              <NoImageAvailable
                v-else
                :departmentName="
                  getProductDepartmentName(
                    product.parent_categories,
                    product.categories,
                  )
                "
                :size="'lg'"
              />
              <CornerRibbon
                v-if="
                  !product.redeemable &&
                  ((product.pricebook_info.promotions &&
                    product.pricebook_info.promotions.length) ||
                    product.new_product)
                "
                :newItem="
                  !(
                    product.pricebook_info.promotions &&
                    product.pricebook_info.promotions.length
                  )
                "
                :large="$vuetify.breakpoint.smAndUp"
              />
            </div>
          </v-col>
          <v-col>
            <v-row>
              <v-col>
                <span class="font-size-lg font-weight-semibold">{{
                  product.name
                }}</span>
                <span class="color-suva-grey">
                  <br />
                  {{ product.brand }}
                </span>
                <UnitSizeDisplay
                  :baseSize="product.base_size"
                  :size="product.size"
                  :uom="product.uom"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <UnitPriceDisplay
                  :byweight="product.byweight"
                  :type="'generic'"
                  :unitPrice="product.cents"
                  :loyalty="product.loyalty"
                  :redeemable="product.redeemable"
                  :points="product.redemption_points_offer"
                />
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="5">
                <CartQuantityButton
                  :cartProduct="cartProduct"
                  :quantityType="'cartQty'"
                />
              </v-col>
              <v-col
                cols="7"
                class="pa-0"
                v-if="
                  !product.redeemable &&
                  product.pricebook_info.promotions &&
                  product.pricebook_info.promotions.length
                "
              >
                <TermsList
                  v-if="
                    product.pricebook_info.promotions &&
                    product.pricebook_info.promotions.length
                  "
                  :byweight="product.byweight"
                  :promotions="product.pricebook_info.promotions"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <Description
              v-if="product.item_description"
              :description="product.item_description"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div
              v-if="product.is_tobacco || product.is_alcohol"
              class="color-suva-grey font-size-sm position-relative"
            >
              <AlcoholWarning v-if="product.is_alcohol" />
              <TobaccoWarning v-if="product.is_tobacco" />
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="hasTieredDiscount"
          class="font-size-xs grey--text justify-center"
        >
          *Tiered discounts cannot be combined with the other tiered discounts
          for this item.
        </v-row>
      </v-container>
    </div>
  </v-dialog>
</template>

<script>
import { getProductDepartmentName } from "@/util.js";
import { mapGetters, mapState } from "vuex";
import AlcoholProductsRequireIdWarning from "@/elements/AlcoholProductsRequireIdWarning.vue";
import AlcoholWarning from "@/components/Product/AlcoholWarning.vue";
import CartQuantityButton from "@/components/Product/CartQuantityButton.vue";
import CloseDialogButton from "@/components/UI/CloseDialogButton.vue";
import Description from '@/components/Product/Description.vue';
import FavoriteIcon from "@/components/Product/FavoriteIcon.vue";
import NoImageAvailable from "@/components/Product/NoImageAvailable.vue";
import CornerRibbon from "@/components/Product/Promotions/CornerRibbon.vue";
import SurgeonGeneralsWarning from "@/components/UI/SurgeonGeneralsWarning.vue";
import TermsList from "@/components/Product/Promotions/Terms/List.vue";
import Title from "@/components/Product/Title.vue";
import TobaccoWarning from "@/components/Product/TobaccoWarning.vue";
import UnitPriceDisplay from "@/components/Product/UnitPriceDisplay.vue";
import UnitSizeDisplay from "@/components/Product/UnitSizeDisplay.vue";

export default {
  name: "ProductDetailsDialog",
  components: {
    AlcoholProductsRequireIdWarning,
    AlcoholWarning,
    CartQuantityButton,
    CloseDialogButton,
    Description,
    FavoriteIcon,
    NoImageAvailable,
    CornerRibbon,
    SurgeonGeneralsWarning,
    TermsList,
    Title,
    TobaccoWarning,
    UnitPriceDisplay,
    UnitSizeDisplay,
  },
  computed: {
    ...mapGetters("departments", {
      getDepartmentDisplayName: "getDepartmentDisplayName",
    }),
    ...mapGetters({
      getProductItemsAndQuantityInCart: "cart/getProductItemsAndQuantityInCart",
      hasTieredDiscount: "product/hasTieredDiscount",
    }),
    ...mapState({
      dialog: (state) => state.product.dialog.details,
      product: (state) => state.product.details,
    }),
    cartProduct() {
      const itemsAndQuantityFromCart = this.getProductItemsAndQuantityInCart(
        this.product,
      );
      return { ...this.product, ...itemsAndQuantityFromCart };
    },
  },
  methods: { getProductDepartmentName },
};
</script>
<style scoped>
.favorite-button-position {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}
</style>
